import React, { useCallback, useEffect, useState } from 'react';
import { Camera, Loader2, RotateCw } from 'lucide-react';
import type { AppTheme, ProfileDTO } from '../../global';
import type { ToastKind } from '../../components/Toast';
import { useLanguage } from '../../lib/i18n';
import { IS_NATIVE, IS_WEB } from '../../lib/platform';
import { useTheme } from '../../lib/theme';
import { useIsNarrow } from '../../lib/useIsNarrow';
import { Switch } from '../../components/ui/Switch';
import { useAutoJoin } from '../calendar/useAutoJoin';
import { MobileSwitch } from '../mobile/MobileUI';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Label } from '../../components/ui/Input';
import { Select, type SelectOption } from '../../components/ui/Select';
import { ProfileDialog } from './ProfileDialog';
import { BehaviorCard } from './BehaviorCard';
import { DeleteAccountCard } from './DeleteAccountCard';
import { NotificationsCard } from './NotificationsCard';
import { UpdatesCard } from './UpdatesCard';

interface GeneralSectionProps {
  /** Profil kaydedilince kenar çubuğundaki ad ve fotoğraf tazelensin. */
  onProfileSaved: () => void;
  onNotify: (kind: ToastKind, text: string) => void;
  onSignOut: () => void;
}

/** Ayarlar > Genel: profil, toplantı botu, görünüm, davranış, güncellemeler, çıkış ve hesap silme. Dil kendi bölümünde. */
export const GeneralSection: React.FC<GeneralSectionProps> = ({ onProfileSaved, onNotify, onSignOut }) => (
  <div className="space-y-6">
    <ProfileSummary onSaved={onProfileSaved} onNotify={onNotify} />
    <AutoBotCard onNotify={onNotify} />
    <AppearanceCard onNotify={onNotify} />
    <BehaviorCard onNotify={onNotify} />
    {/* Web her açılışta en son sürüm — güncelleme kartı yalnızca masaüstünde. */}
    {!IS_WEB && <UpdatesCard onNotify={onNotify} />}
    {IS_NATIVE && <NotificationsCard />}
    <DeleteAccountCard onNotify={onNotify} onSignOut={onSignOut} />
  </div>
);

/** Solda etiket + açıklama, sağda kontrol — ayar satırlarının ortak düzeni. */
const SettingRow: React.FC<{
  label: string;
  hint: string;
  labelId: string;
  hintId: string;
  children: React.ReactNode;
}> = ({ label, hint, labelId, hintId, children }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <div className="space-y-1.5">
      <Label id={labelId}>{label}</Label>
      <p id={hintId} className="text-[0.8rem] text-muted-foreground">
        {hint}
      </p>
    </div>
    {children}
  </div>
);

// ————————————————————————————————————————————————
// Toplantı botu
// ————————————————————————————————————————————————

/**
 * Otomatik bot katılımının ana düğmesi — Ana sayfadaki takvim kartındakiyle
 * aynı ayar (yeni hesaplarda açık). Toplantılarda tek tek yapılan seçimlere
 * dokunmaz (kural: src/shared/autoJoin.ts).
 */
const AutoBotCard: React.FC<{ onNotify: GeneralSectionProps['onNotify'] }> = ({ onNotify }) => {
  const { t } = useLanguage();
  const autoJoin = useAutoJoin();
  const isNarrow = useIsNarrow();

  const change = (enabled: boolean) => {
    autoJoin.setEnabled(enabled).catch((err: Error) => {
      onNotify('error', err.message || t('Ayar kaydedilemedi.', 'Could not save the setting.'));
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('Toplantı botu', 'Meeting bot')}</CardTitle>
        <CardDescription>
          {t('Takvimindeki toplantılara botun kendiliğinden katılıp katılmayacağı.', 'Whether the bot joins your calendar meetings on its own.')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1.5">
            <Label id="auto-bot-label">{t('Otomatik bot katılımı', 'Automatic bot joining')}</Label>
            <p id="auto-bot-hint" className="text-[0.8rem] text-muted-foreground">
              {autoJoin.enabled
                ? t(
                    'Açık: bağlantısı olan toplantılara bot kendiliğinden katılır. Tek bir toplantıyı takvimden kapatabilirsin.',
                    'On: the bot joins meetings that have a link. You can skip a single meeting from the calendar.'
                  )
                : t('Kapalı: botu her toplantı için kendin gönderirsin.', 'Off: you send the bot to each meeting yourself.')}
            </p>
          </div>
          {isNarrow ? (
            <MobileSwitch checked={autoJoin.enabled} disabled={!autoJoin.loaded} onChange={change} labelledBy="auto-bot-label" />
          ) : (
            <Switch
              checked={autoJoin.enabled}
              disabled={!autoJoin.loaded}
              onChange={change}
              label={t('Otomatik bot katılımı', 'Automatic bot joining')}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ————————————————————————————————————————————————
// Görünüm
// ————————————————————————————————————————————————

/** Tema seçimi. Üst bardaki güneş/ay düğmesi de aynı tercihi yazıyor. */
const AppearanceCard: React.FC<{ onNotify: GeneralSectionProps['onNotify'] }> = ({ onNotify }) => {
  const { t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const [isSaving, setIsSaving] = useState(false);
  // Telefonda üst bar ve güneş/ay düğmesi yok.
  const isNarrow = useIsNarrow();

  const options: SelectOption<AppTheme>[] = [
    {
      value: 'system',
      label: t('Sistem', 'System'),
      hint: t('İşletim sistemini izler', 'Follows your operating system'),
    },
    { value: 'light', label: t('Açık', 'Light') },
    { value: 'dark', label: t('Koyu', 'Dark') },
  ];

  const change = async (next: AppTheme) => {
    setIsSaving(true);
    try {
      await setTheme(next);
    } catch {
      onNotify('error', t('Tema kaydedilemedi.', 'Could not save the theme.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('Görünüm', 'Appearance')}</CardTitle>
        <CardDescription>
          {t('Uygulamanın açık mı koyu mu görüneceği.', 'Whether the app looks light or dark.')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <SettingRow
          label={t('Tema', 'Theme')}
          hint={
            isNarrow
              ? t('Sistem seçiliyse telefonun temasını izler.', 'On System, it follows your phone’s theme.')
              : t(
                  'Üst bardaki güneş/ay düğmesi de bu ayarı değiştirir.',
                  'The sun/moon button in the top bar changes this setting too.'
                )
          }
          labelId="app-theme-label"
          hintId="app-theme-hint"
        >
          <Select
            id="app-theme"
            aria-labelledby="app-theme-label"
            aria-describedby="app-theme-hint"
            value={theme}
            options={options}
            onChange={(next) => void change(next)}
            disabled={isSaving}
            className="w-full shrink-0 sm:w-44"
          />
        </SettingRow>
      </CardContent>
    </Card>
  );
};

// ————————————————————————————————————————————————
// Profil
// ————————————————————————————————————————————————

/**
 * Profilin tek satırlık özeti: fotoğraf, ad, e-posta ve şifre. Düzenleme
 * "Düzenle" düğmesiyle açılan pencerede (bkz. ProfileDialog).
 */
const ProfileSummary: React.FC<{ onSaved: () => void; onNotify: GeneralSectionProps['onNotify'] }> = ({
  onSaved,
  onNotify,
}) => {
  const { t } = useLanguage();
  const [profile, setProfile] = useState<ProfileDTO | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isNarrow = useIsNarrow();

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setProfile(await window.zabit.getProfile());
    } catch (err) {
      setLoadError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!profile) {
    return (
      <Card>
        <CardContent>
          {loadError ? (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-destructive">{loadError}</p>
              <Button variant="outline" size="sm" onClick={() => void load()}>
                <RotateCw className="size-4" />
                {t('Tekrar dene', 'Try again')}
              </Button>
            </div>
          ) : (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {t('Profil yükleniyor…', 'Loading profile…')}
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  const dialog = (
    <ProfileDialog
      open={isDialogOpen}
      profile={profile}
      onClose={() => setIsDialogOpen(false)}
      onSaved={(next) => {
        setProfile(next);
        onSaved();
      }}
      onNotify={onNotify}
    />
  );

  // Telefon: ortada büyük fotoğraf (köşesindeki kamera düzenlemeyi açar), ad, e-posta.
  if (isNarrow) {
    return (
      <>
        <Card>
          <CardContent className="flex flex-col items-center gap-3.5 text-center">
            <button
              type="button"
              onClick={() => setIsDialogOpen(true)}
              aria-label={t('Fotoğrafı ve profili düzenle', 'Edit photo and profile')}
              className="relative rounded-full active:opacity-80"
            >
              <Avatar
                src={profile.avatarUrl ?? profile.googleAvatarDataUrl ?? null}
                name={profile.displayName || profile.email}
                className="size-[92px] text-[28px]"
              />
              <span className="absolute -right-1 -bottom-1 w-[34px] h-[34px] rounded-full bg-brand text-on-brand border-[3px] border-card flex items-center justify-center">
                <Camera className="w-4 h-4" />
              </span>
            </button>
            <div className="space-y-0.5 min-w-0 max-w-full">
              <p className="text-[19px] font-bold truncate">{profile.displayName}</p>
              <p className="text-[14px] text-muted-foreground truncate">{profile.email}</p>
            </div>
            <Button className="w-full h-11 rounded-full text-[15px]" onClick={() => setIsDialogOpen(true)}>
              {t('Profili düzenle', 'Edit profile')}
            </Button>
          </CardContent>
        </Card>
        {dialog}
      </>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <Avatar
              src={profile.avatarUrl ?? profile.googleAvatarDataUrl ?? null}
              name={profile.displayName || profile.email}
              className="size-12 text-base"
            />
            <div className="min-w-0 space-y-1.5">
              <p className="truncate font-semibold leading-none">{profile.displayName}</p>
              <p className="truncate text-sm text-muted-foreground underline underline-offset-2">{profile.email}</p>
              <p className="text-sm text-muted-foreground">
                {profile.hasPassword ? (
                  <span className="tracking-[0.2em]">••••••••</span>
                ) : (
                  t('Şifre belirlenmedi', 'No password set')
                )}
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => setIsDialogOpen(true)}>
            {t('Düzenle', 'Edit')}
          </Button>
        </CardContent>
      </Card>

      {dialog}
    </>
  );
};
